import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Header from "../Header/header";
import Fotter from "../Fotter/fotter";
import {
    CheckCircle,
    ArrowRight,
    Home,
    Calendar as CalendarIcon,
    MapPin,
    PartyPopper,
    Mail,
    Send,
    Loader2
} from "lucide-react";
import { toast } from "react-toastify";
import API_URL from "../../config";


function BookingSuccessPage() {
    const { state } = useLocation();
    const navigate = useNavigate();
    const [email, setEmail] = useState(state?.email || "");
    const [isSending, setIsSending] = useState(false);
    const [sent, setSent] = useState(false);

    // Defensive check
    if (!state) {
        return (
            <div className="min-h-screen bg-[#F8FAFC] flex flex-col">
                <Header />
                <main className="flex-grow flex items-center justify-center">
                    <button onClick={() => navigate("/")} className="px-8 py-4 bg-sky-500 text-white rounded-2xl font-bold">Return Home</button>
                </main>
                <Fotter />
            </div>
        );
    }

    const handleSendReceipt = async () => {
        if (!email) {
            toast.error("Please enter an email address");
            return;
        }

        setIsSending(true);
        try {
            const response = await fetch(`${API_URL}/bookings/send-receipt`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    email,
                    userName: state.userName,
                    accommodationName: state.accommodationName || "Accommodation",
                    qty: state.qty,
                    totalPrice: state.totalPrice
                }),
            });

            if (response.ok) {
                toast.success("Digital receipt sent successfully!");
                setSent(true);
            } else {
                const data = await response.json();
                toast.error(data.message || "Failed to send receipt");
            }
        } catch (error) {
            console.error("Receipt error:", error);
            toast.error("Something went wrong. Please try again.");
        } finally {
            setIsSending(false);
        }
    };

    return (
        <div className="min-h-screen bg-[#F8FAFC] flex flex-col font-sans">
            <Header />

            <main className="flex-grow flex items-center justify-center py-20 px-4">
                <div className="max-w-2xl w-full bg-white rounded-[3rem] shadow-2xl shadow-sky-900/10 border border-sky-100/50 p-12 text-center relative overflow-hidden">

                    {/* Decorative Background Elements */}
                    <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-sky-400 via-teal-400 to-sky-500"></div>
                    <div className="absolute -top-24 -right-24 w-64 h-64 bg-sky-50 rounded-full blur-3xl opacity-50"></div>
                    <div className="absolute -bottom-24 -left-24 w-64 h-64 bg-teal-50 rounded-full blur-3xl opacity-50"></div>

                    <div className="relative z-10">
                        <div className="w-24 h-24 bg-emerald-500 rounded-[2.5rem] flex items-center justify-center text-white mx-auto mb-8 shadow-2xl shadow-emerald-500/40 animate-bounce">
                            <CheckCircle size={48} />
                        </div>

                        <div className="space-y-4 mb-10">
                            <div className="inline-flex items-center gap-2 px-4 py-2 bg-sky-50 rounded-full border border-sky-100 text-sky-600">
                                <PartyPopper size={16} />
                                <span className="text-[10px] font-black uppercase tracking-widest">Journey Confirmed</span>
                            </div>
                            <h1 className="text-4xl font-black text-sky-950 tracking-tight">Booking Successful!</h1>
                            <p className="text-gray-500 font-medium max-w-sm mx-auto">
                                Thank you for choosing TravelAura, <span className="text-sky-600 font-bold">{state.userName}</span>. Your adventure in Sri Lanka starts here!
                            </p>
                        </div>

                        <div className="bg-gray-50 rounded-[2rem] p-8 mb-8 space-y-4 border border-gray-100 text-left">
                            <div className="flex justify-between items-center text-sm font-bold">
                                <div className="flex items-center gap-2 text-gray-400">
                                    <MapPin size={16} className="text-sky-500" />
                                    <span>Accommodation</span>
                                </div>
                                <span className="text-sky-950 font-black">{state.accommodationName || "Confirmed Stay"}</span>
                            </div>
                            <div className="flex justify-between items-center text-sm font-bold">
                                <div className="flex items-center gap-2 text-gray-400">
                                    <CalendarIcon size={16} className="text-sky-500" />
                                    <span>Guests</span>
                                </div>
                                <span className="text-sky-950 font-black">{state.qty} Guests</span>
                            </div>
                            <div className="pt-4 border-t border-gray-200 mt-2 flex justify-between items-center">
                                <span className="text-xs font-black text-gray-400 uppercase tracking-widest">Amount Paid</span>
                                <span className="text-2xl font-black text-emerald-600">LKR {state.totalPrice?.toLocaleString()}</span>
                            </div>
                        </div>

                        {/* Digital Receipt Section */}
                        <div className="mb-10 p-8 bg-sky-50/50 border border-sky-100 rounded-[2rem] relative group overflow-hidden">
                            <div className="absolute top-0 right-0 p-4 opacity-10 group-hover:opacity-20 transition-opacity">
                                <Mail size={80} className="text-sky-900" />
                            </div>

                            <h3 className="text-lg font-black text-sky-950 mb-2 flex items-center justify-center gap-2">
                                <Mail size={20} className="text-sky-500" />
                                Digital Receipt
                            </h3>
                            <p className="text-xs font-bold text-gray-400 mb-6">Enter your email and we'll send your confirmation details instantly.</p>

                            <div className="relative max-w-md mx-auto">
                                <input
                                    type="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    placeholder="your-email@example.com"
                                    className="w-full pl-6 pr-32 py-4 bg-white border border-sky-100 rounded-2xl focus:ring-4 focus:ring-sky-500/10 focus:border-sky-400 transition-all font-bold text-sky-950 placeholder:text-gray-300 shadow-sm"
                                />
                                <button
                                    onClick={handleSendReceipt}
                                    disabled={isSending || sent}
                                    className={`absolute right-2 top-2 bottom-2 px-6 rounded-xl font-black text-[10px] uppercase tracking-widest flex items-center gap-2 transition-all shadow-lg
                                        ${sent
                                            ? 'bg-emerald-500 text-white shadow-emerald-500/20 cursor-default'
                                            : 'bg-sky-600 text-white shadow-sky-600/20 hover:bg-sky-700 active:scale-95'
                                        } ${isSending ? 'opacity-70' : ''}`}
                                >
                                    {isSending ? (
                                        <Loader2 size={16} className="animate-spin" />
                                    ) : sent ? (
                                        <>Sent <CheckCircle size={14} /></>
                                    ) : (
                                        <>Send <Send size={14} /></>
                                    )}
                                </button>
                            </div>
                        </div>

                        <div className="flex flex-col sm:flex-row gap-4 items-center justify-center">
                            <button
                                onClick={() => navigate("/")}
                                className="w-full sm:w-auto px-10 py-4 bg-sky-950 text-white rounded-2xl font-black text-sm hover:bg-black transition-all flex items-center justify-center gap-2 group shadow-xl shadow-sky-900/10"
                            >
                                <Home size={18} />
                                Back to Explore
                            </button>
                            <button
                                onClick={() => navigate("/user-dashboard")}
                                className="w-full sm:w-auto px-10 py-4 bg-white text-sky-600 border border-sky-100 rounded-2xl font-black text-sm hover:bg-sky-50 transition-all flex items-center justify-center gap-2 group shadow-xl shadow-sky-900/5"
                            >
                                View Dashboard
                                <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
                            </button>
                        </div>
                    </div>
                </div>
            </main>

            <Fotter />
        </div>
    );
}

export default BookingSuccessPage;
